import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PeopleOutlined from "@mui/icons-material/PeopleOutlined";
import { useTranslationApp } from "../../lib/i18next";
import {
  APPROVAL_REVIEWER_LABEL,
  APPROVAL_REVIEWERS_SECTION_LABEL,
  APPROVAL_REVIEWER_STATUS_APPROVED,
  APPROVAL_REVIEWER_STATUS_PENDING,
  APPROVAL_REVIEWER_STATUS_REJECTED,
} from "../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import ContentApprovalStatusChip from "./ContentApprovalStatusChip";
import ReviewerUrlCopyButton from "./ReviewerUrlCopyButton";
import {
  getApprovalReviewersList,
  hasMultipleApprovalReviewers,
} from "./approvalReviewersUtils";

const PRIMARY_APPROVAL_REVIEWER_NUMBER = 1;

const MAX_VISIBLE_REVIEWERS = 3;
const REVIEWER_ROW_HEIGHT = 40;
const REVIEWER_ROW_WITH_URL_HEIGHT = 64;

const ApprovalReviewersStatus = ({ reviewers = [] }) => {
  const { t } = useTranslationApp();

  if (!hasMultipleApprovalReviewers(reviewers)) {
    return null;
  }

  const getReviewerStatusLabel = (status) => {
    if (status === "approved") {
      return t(APPROVAL_REVIEWER_STATUS_APPROVED);
    }

    if (status === "rejected") {
      return t(APPROVAL_REVIEWER_STATUS_REJECTED);
    }

    return t(APPROVAL_REVIEWER_STATUS_PENDING);
  };

  const reviewersList = getApprovalReviewersList({ reviewers });

  const sortedReviewers = [...reviewersList].sort((reviewerA, reviewerB) => {
    const hasEmailA = Boolean(reviewerA?.email?.trim());
    const hasEmailB = Boolean(reviewerB?.email?.trim());

    if (hasEmailA !== hasEmailB) {
      return hasEmailA ? 1 : -1;
    }

    return String(reviewerA?.reviewerID || "").localeCompare(
      String(reviewerB?.reviewerID || "")
    );
  });

  const getReviewerRowHeight = (reviewer) =>
    reviewer?.url?.trim() ? REVIEWER_ROW_WITH_URL_HEIGHT : REVIEWER_ROW_HEIGHT;

  const shouldScroll = sortedReviewers.length > MAX_VISIBLE_REVIEWERS;
  const reviewersStatusListMaxHeight = sortedReviewers
    .slice(0, MAX_VISIBLE_REVIEWERS)
    .reduce(
      (totalHeight, reviewer) => totalHeight + getReviewerRowHeight(reviewer),
      0
    );

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PeopleOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {t(APPROVAL_REVIEWERS_SECTION_LABEL)}
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          borderRadius: 2,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          overflow: shouldScroll ? "auto" : "hidden",
          maxHeight: shouldScroll ? reviewersStatusListMaxHeight : undefined,
          pr: shouldScroll ? 0.5 : 0,
        }}
      >
        {sortedReviewers.map((reviewer, index) => {
          const label = reviewer?.email?.trim()
            ? reviewer.email
            : t(APPROVAL_REVIEWER_LABEL, {
                number: PRIMARY_APPROVAL_REVIEWER_NUMBER,
              });
          const isLastRow = index === sortedReviewers.length - 1;
          const reviewerUrl = reviewer?.url || "";

          return (
            <Box
              component="li"
              key={reviewer.reviewerID || `${label}-${index}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.2,
                minHeight: getReviewerRowHeight(reviewer),
                px: 1,
                py: 1,
                borderBottom: isLastRow
                  ? "none"
                  : `1px solid ${INPUT_COLOR_BORDER}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
                <ContentApprovalStatusChip
                  status={reviewer?.status || "pending"}
                  label={getReviewerStatusLabel(reviewer?.status || "pending")}
                />
              </Box>
              <ReviewerUrlCopyButton url={reviewerUrl} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ApprovalReviewersStatus;
